// A SZÁMLA TÉTELEI + a számlára kerülő megjegyzés — két helyen jelenik meg (a havi áttekintés kibontott
// számlája és a Számláim lista tétel-panelje), ezért közös. Csak megjelenítés: az összegek a szerverről jönnek.
import type { PortalInvoiceDetail } from '@atlas/contracts';
import { useTranslation } from 'react-i18next';

import { formatMoney } from '@/components/shared/money-block';
import { Text } from '@/components/ui/text';
import { formatDayRange, formatQuantity } from '@/features/portal/billing-format';

export function PortalInvoiceItems({ invoice }: { invoice: PortalInvoiceDetail }): JSX.Element {
  const { t, i18n } = useTranslation();
  const money = (v: string): string => formatMoney(v, invoice.currency, i18n.language);
  return (
    <div className="space-y-3">
      {invoice.items.length === 0 ? (
        <Text variant="secondary" as="p">
          {t('portal.billing.invoice.noItems')}
        </Text>
      ) : (
        <ul aria-label={t('portal.billing.invoice.itemsAria')} className="space-y-2">
          {invoice.items.map((it, i) => {
            const period = formatDayRange(it.periodStart, it.periodEnd, i18n.language);
            return (
              <li
                key={`${it.name}-${i}`}
                className="bg-sunken flex flex-col gap-1.5 rounded-lg px-3 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
              >
                <div className="min-w-0 space-y-0.5">
                  <Text variant="itemTitle" as="p">
                    {it.name}
                  </Text>
                  {period !== null && (
                    <Text variant="secondary" as="p">
                      {t('portal.billing.invoice.period')}: {period}
                    </Text>
                  )}
                  <Text variant="secondary" as="p" className="font-mono tabular-nums">
                    {t('portal.billing.invoice.quantity')}: {formatQuantity(it.quantity)}
                    {it.quantityUnit
                      ? ` ${t(`invoices.quantityUnitLabel.${it.quantityUnit}`, { defaultValue: it.quantityUnit })}`
                      : ''}{' '}
                    × {money(it.unitPrice)}
                  </Text>
                  {it.note !== null && it.note !== '' && (
                    <Text variant="secondary" as="p">
                      {it.note}
                    </Text>
                  )}
                </div>
                <div className="flex shrink-0 flex-col sm:items-end">
                  <span className="font-mono text-[13.5px] font-semibold tabular-nums">
                    {money(it.net)}
                  </span>
                  <span className="text-muted-foreground font-mono text-[11px] tabular-nums">
                    {t('money.vat')} {money(it.vat)} · {t('money.gross')} {money(it.gross)}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {invoice.note !== null && invoice.note !== '' && (
        <div className="border-border rounded-lg border px-3 py-2.5">
          <Text variant="overline" as="p">
            {t('portal.billing.invoice.note')}
          </Text>
          <Text variant="body" as="p" className="mt-1 whitespace-pre-line">
            {invoice.note}
          </Text>
        </div>
      )}
    </div>
  );
}
